import _ from 'underscore';
import { DependencyResolver } from '@bingads-webui-campaign/dependency-resolver';
import { FeedBasedCampaginSubTypes, CampaignSubTypeMapping, BidStrategy } from '@bingads-webui-campaign/audience-campaign-shared';

// Age Target Enum Map between Campaign and AdInsights
const ageTargetMap = {
  EighteenToTwentyFive: 'EighteenToTwentyFour',
  TwentyFiveToThirtyFive: 'TwentyFiveToThirtyFour',
  ThirtyFiveToFifty: 'ThirtyFiveToFourtyNine',
  FiftyToSixtyFive: 'FiftyToSixtyFour',
  SixtyFiveAndAbove: 'SixtyFiveAndAbove',
  Unknown: 'Unknown',
};

/**
 * Estimate Request Factory
 * This serves to create the request for AudienceEstimation and Privacy Check calls to AdInsights
 *
 * @class EstimateRequestFactory
 */
export class EstimateRequestFactory {
  constructor(options = {}) {
    this.currentLevelTargetInfo = options.currentLevelTargetInfo;
    this.parentLevelTargetInfo = options.parentLevelTargetInfo;
    const permissionConfig = DependencyResolver.get('component/config/index');
    this.permissions = permissionConfig.get('Permissions');
    this.bidStrategyPermissionBind = {
      [BidStrategy.ManualCpc]: true,
      [BidStrategy.EnhancedCpc]: true,
      [BidStrategy.ManualCpv]: this.permissions.IsKPIEstimateForCPVEnabled,
      [BidStrategy.ManualCpm]: this.permissions.IsKPIEstimateForCPMEnabled,
    };
    this.campaignSubTypesSupported = [
      CampaignSubTypeMapping.AudienceStatic,
      CampaignSubTypeMapping.AudienceVideoAds,
    ];
    if (this.permissions.IsKPIEstimateForAudienceFeedBasedEnabled) {
      this.campaignSubTypesSupported =
        this.campaignSubTypesSupported.concat(FeedBasedCampaginSubTypes);
    }

    this.request = {};
  }

  /**
   * Creates the request for AudienceEstimation and Privacy Check calls
   *
   * @param {number} bidStrategy bid strategy enum used to create request
   * @param {number} campaignSubType sub type enum of campaign used to create request
   * @param {object} feedInfo audience campaign feed information
   * @param {object} useOptimizedTargeting if use Optimized Targeting
   *
   * @returns {object} request
   */
  create(bidStrategy, campaignSubType, feedInfo, useOptimizedTargeting) {
    this.request = {};

    return this.createWithTargetSettings(['CompanyName', 'Industry', 'JobFunction', 'Audience'], bidStrategy, campaignSubType, feedInfo, useOptimizedTargeting);
  }

  /**
   * Creates the request for AudienceEstimation and Privacy Check calls
   * This takes into account the user's TargetAndBid vs BidOnly selection
   *
   * @param {Array} targetSettings targetSettings
   * @param {number} bidStrategy bid strategy enum used to create request
   * @param {number} campaignSubType sub type enum of campaign used to create request
   * @param {object} feedInfo audience campaign feed information
   * @param {object} useOptimizedTargeting if use Optimized Targeting
   *
   * @returns {object} request
   */
  createWithTargetSettings(
    targetSettings, bidStrategy, campaignSubType,
    feedInfo, useOptimizedTargeting
  ) {
    this.request = {};

    this.addAgeTargets();
    this.addGenderTargets();
    this.addLocationTargets();
    this.addRadiusTargets();

    // Add Included Targets only if Target & Bid
    this.addCompanyTargets(_.contains(targetSettings, 'CompanyName'));
    this.addIndustryTargets(_.contains(targetSettings, 'Industry'));
    this.addJobFunctionTargets(_.contains(targetSettings, 'JobFunction'));
    this.addAudienceTargets(_.contains(targetSettings, 'Audience'));

    if (_.isNumber(bidStrategy) && this.bidStrategyPermissionBind[bidStrategy]) {
      this.request.CampaignBiddingStrategy = bidStrategy;
    } else {
      this.request.CampaignBiddingStrategy = BidStrategy.ManualCpc;
    }
    let subType = campaignSubType;
    // We convert it to enum number first
    if (_.isString(subType)) {
      subType = CampaignSubTypeMapping[campaignSubType];
    }
    if ((_.isNumber(subType) && this.campaignSubTypesSupported.includes(subType))) {
      this.request.CampaignSubType = subType;
    } else {
      this.request.CampaignSubType = CampaignSubTypeMapping.AudienceStatic;
    }

    if (FeedBasedCampaginSubTypes.includes(this.request.CampaignSubType)) {
      this.request.Feed = {
        StoreId: _.get(feedInfo, 'StoreId'),
        FeedId: _.get(feedInfo, 'FeedId'),
      };
    }

    this.request.AutoTargeting = useOptimizedTargeting;
    return this.request;
  }

  /**
   * @private
   *
   * @returns {undefined}
   */
  addAgeTargets() {
    const criterions = this.currentLevelTargetInfo.getAllByType('Age');

    if (_.isEmpty(criterions)) {
      return;
    }

    const targets = { Includes: [], Excludes: [] };

    _.each(criterions, (c) => {
      if (c.CriterionType === 'Age') {
        const translatedAgeTarget = ageTargetMap[c.Criterion.AgeCriterion.Target];

        targets.Includes.push(translatedAgeTarget);
      } else if (c.CriterionType === 'NegativeAge') {
        const translatedAgeTarget = ageTargetMap[c.Criterion.AgeCriterion.Target];

        targets.Excludes.push(translatedAgeTarget);
      }
    });

    this.request.Age = targets;
  }

  /**
   * @private
   *
   * @returns {undefined}
   */
  addGenderTargets() {
    const criterions = this.currentLevelTargetInfo.getAllByType('Gender');

    if (_.isEmpty(criterions)) {
      return;
    }

    const targets = { Includes: [], Excludes: [] };

    _.each(criterions, (c) => {
      if (c.CriterionType === 'Gender') {
        targets.Includes.push(c.Criterion.GenderCriterion.Target);
      } else if (c.CriterionType === 'NegativeGender') {
        targets.Excludes.push(c.Criterion.GenderCriterion.Target);
      }
    });

    this.request.Gender = targets;
  }

  /**
   * @private
   *
   * @returns {undefined}
   */
  addLocationTargets() {
    let targetInfoToUse = this.currentLevelTargetInfo;

    if (this.parentLevelTargetInfo &&
      this.currentLevelTargetInfo.parentLevelTogglesByType.Location) {
      targetInfoToUse = this.parentLevelTargetInfo;
    }

    const includedCriterions = targetInfoToUse.getAllByType('Location');
    const excludedCriterions = targetInfoToUse.getAllByType('NegativeLocation');

    if (_.isEmpty(includedCriterions) && _.isEmpty(excludedCriterions)) {
      // If no location criterions found, then target 'All locations'
      this.request.Location = {};

      return;
    }

    const targets = { Includes: [], Excludes: [] };

    _.each(includedCriterions, (c) => {
      const locationCriterion = c.Criterion.LocationCriterion;

      if (locationCriterion) {
        if (c.CriterionType === 'NegativeLocation') {
          targets.Excludes.push(locationCriterion.LocationId);
        } else {
          targets.Includes.push(locationCriterion.LocationId);
        }
      } else if (c['@odata.type'] === '#Model.NegativeCampaignCriterion') { // For campaign inherited criterions
        targets.Excludes.push(c.Criterion.LocationId);
      } else if (c['@odata.type'] === '#Model.CampaignCriterion') {
        targets.Includes.push(c.Criterion.LocationId);
      }
    });

    _.each(excludedCriterions, (c) => {
      const excludedLocationCriterion = c.Criterion.LocationCriterion;

      if (excludedLocationCriterion) {
        targets.Excludes.push(excludedLocationCriterion.LocationId);
      }
    });

    this.request.Location = targets;
  }

  /**
   * @private
   *
   * @returns {undefined}
   */
  addRadiusTargets() {
    let targetInfoToUse = this.currentLevelTargetInfo;

    if (this.parentLevelTargetInfo &&
      this.currentLevelTargetInfo.parentLevelTogglesByType.Location) {
      targetInfoToUse = this.parentLevelTargetInfo;
    }

    const radiusCriterions = targetInfoToUse.getAllByType('Radius');

    if (_.isEmpty(radiusCriterions)) {
      return;
    }

    const radiusTargets = [];

    _.each(radiusCriterions, (c) => {
      const radiusCriterion = c.Criterion.RadiusCriterion;

      if (radiusCriterion) {
        radiusTargets.push({
          Radius: radiusCriterion.Radius,
          CenterLongitude: radiusCriterion.Longitude,
          CenterLatitude: radiusCriterion.Latitude,
        });
      }
    });

    if (radiusTargets.length > 0) {
      this.request.RadiusTargets = radiusTargets;
    }
  }

  /**
   * @private
   * @param {bool} addInclusions addInclusions
   * @returns {undefined}
   */
  addCompanyTargets(addInclusions) {
    const criterions = this.currentLevelTargetInfo.getAllByType('CompanyName');

    if (_.isEmpty(criterions)) {
      return;
    }

    const targets = { Includes: [], Excludes: [] };
    const arr = _.partition(criterions, c => c.CriterionType === 'CompanyName');
    const includedCriterions = arr[0];
    const excludedCriterions = arr[1];

    if (addInclusions) {
      _.each(includedCriterions, (c) => {
        targets.Includes.push(c.Criterion.CompanyNameCriterion.Target);
      });
    }

    _.each(excludedCriterions, (c) => {
      targets.Excludes.push(c.Criterion.CompanyNameCriterion.Target);
    });

    this.request.Company = targets;
  }

  /**
   * @private
   * @param {bool} addInclusions addInclusions
   * @returns {undefined}
   */
  addJobFunctionTargets(addInclusions) {
    const criterions = this.currentLevelTargetInfo.getAllByType('JobFunction');

    if (_.isEmpty(criterions)) {
      return;
    }

    const targets = { Includes: [], Excludes: [] };
    const arr = _.partition(criterions, c => c.CriterionType === 'JobFunction');
    const includedCriterions = arr[0];
    const excludedCriterions = arr[1];

    if (addInclusions) {
      _.each(includedCriterions, (c) => {
        targets.Includes.push(c.Criterion.JobFunctionCriterion.Target);
      });
    }

    _.each(excludedCriterions, (c) => {
      targets.Excludes.push(c.Criterion.JobFunctionCriterion.Target);
    });

    this.request.JobFunction = targets;
  }

  /**
   * @private
   * @param {bool} addInclusions addInclusions
   * @returns {undefined}
   */
  addIndustryTargets(addInclusions) {
    const criterions = this.currentLevelTargetInfo.getAllByType('Industry');

    if (_.isEmpty(criterions)) {
      return;
    }

    const targets = { Includes: [], Excludes: [] };
    const arr = _.partition(criterions, c => c.CriterionType === 'Industry');
    const includedCriterions = arr[0];
    const excludedCriterions = arr[1];

    if (addInclusions) {
      _.each(includedCriterions, (c) => {
        targets.Includes.push(c.Criterion.IndustryCriterion.Target);
      });
    }

    _.each(excludedCriterions, (c) => {
      targets.Excludes.push(c.Criterion.IndustryCriterion.Target);
    });

    this.request.Industry = targets;
  }

  /**
   * @private
   * @param {bool} addInclusions addInclusions
   * @returns {undefined}
   */
  addAudienceTargets(addInclusions) {
    let criterions = this.currentLevelTargetInfo.getAllByType('Audience');

    criterions = _.reject(criterions, c => c.Status === 'Paused');

    if (_.isEmpty(criterions)) {
      return;
    }

    const targets = { Includes: [], Excludes: [] };
    const arr = _.partition(criterions, c => c.Status === 'Excluded');

    const excludedCriterions = arr[0];
    const includedCriterions = arr[1];

    if (addInclusions) {
      _.each(includedCriterions, (c) => {
        targets.Includes.push(c.Criterion.AudienceId);
      });
    }

    _.each(excludedCriterions, (c) => {
      targets.Excludes.push(c.Criterion.AudienceId);
    });

    this.request.Audience = targets;
  }
}
